import { bucketErrorCode, externalServiceErrorCode, workflowErrorCode } from "@workspace/schemas/errors";

// Also used client-side by `DocumentLinkingPopover`'s disabled-button tooltip.
export const INVOICE_INCIDENT_NOT_LOCKED_MESSAGE =
  "Für diesen Task wurde noch kein Beleg erfasst – Dokumente können erst nach der ersten Belegerfassung verlinkt werden.";

/** Maps machine-readable API error codes to German user-facing messages */
// NOTE: We can restructure this in the future into multiple files if the number of error codes grows.
const ERROR_MESSAGES: Record<string, string> = {
  // Bucket status validation
  [bucketErrorCode.ALREADY_ENDED]: "Bucket wurde bereits beendet",
  [bucketErrorCode.ALREADY_STARTED]: "Bucket wurde bereits gestartet",
  [bucketErrorCode.FIXED_START_NOT_REACHED]: "Bucket kann noch nicht gestartet werden (feste Startzeit)",
  [bucketErrorCode.PLANNED_END_EXCEEDED]: "Bucket kann nicht mehr gestartet werden (geplante Endzeit überschritten)",
  [bucketErrorCode.NOT_YET_STARTED]: "Bucket wurde noch nicht gestartet",
  [bucketErrorCode.CONCURRENT_IN_PROGRESS]: "Es gibt bereits einen laufenden Bucket für diesen Tag",
  [bucketErrorCode.EARLIER_BUCKET_EXISTS]: "Es gibt einen früheren startbaren Bucket, der zuerst vorgesehen ist",
  [bucketErrorCode.TIME_CONFLICT]: "Zeitüberschneidung mit einem bestehenden Bucket",
  [bucketErrorCode.DELETE_NOT_ALLOWED]: "Bucket ist in diesem Zustand nicht löschbar",
  [bucketErrorCode.EDIT_NOT_ALLOWED]: "Bucket kann in diesem Zustand nicht bearbeitet werden",
  [bucketErrorCode.HAS_INCOMPLETE_TASKS]: "Bucket kann nicht abgeschlossen werden, solange noch offene Tasks vorhanden sind",
  // Workflow errors
  [workflowErrorCode.STEP_NOT_FOUND]: "Step nicht gefunden",
  [workflowErrorCode.STEP_TASK_MISMATCH]: "Step gehört nicht zu diesem Task",
  [workflowErrorCode.STEP_TYPE_MISMATCH]: "Step-Typ stimmt nicht überein",
  [workflowErrorCode.STEP_ALREADY_COMPLETED]: "Step wurde bereits abgeschlossen",
  [workflowErrorCode.UNKNOWN_STEP_TYPE]: "Unbekannter Step-Typ",
  [workflowErrorCode.TASK_NOT_FOUND]: "Task nicht gefunden",
  [workflowErrorCode.TASK_ASSIGNEE_MISMATCH]: "Task ist einem anderen Benutzer zugewiesen",
  [workflowErrorCode.TASK_BUCKET_MISMATCH]: "Task gehört nicht zu diesem Bucket",
  [workflowErrorCode.TASK_ALREADY_TERMINAL]: "Task wurde bereits abgeschlossen",
  [workflowErrorCode.BUCKET_NOT_FOUND]: "Bucket nicht gefunden",
  [workflowErrorCode.BUCKET_ASSIGNEE_MISMATCH]: "Bucket ist einem anderen Benutzer zugewiesen",
  [workflowErrorCode.BUCKET_NOT_IN_PROGRESS]: "Bucket ist nicht aktiv",
  [workflowErrorCode.BUCKET_ALREADY_TERMINAL]: "Bucket wurde bereits beendet",
  [workflowErrorCode.INVALID_BUCKET_TYPE]: "Bucket akzeptiert diesen Aufgabentyp nicht",
  [workflowErrorCode.TASK_ALREADY_IN_PROGRESS]: "Für diesen Bucket ist bereits ein Task in Bearbeitung",
  [workflowErrorCode.STEP_NO_LONGER_CURRENT]:
    "Die Ansicht ist veraltet – der Task hat noch einen anderen offenen Schritt. Bitte Ansicht aktualisieren und erneut versuchen.",
  [workflowErrorCode.IMPLEMENTATION_MISSING]: "Dieser Step-Typ wird noch nicht unterstützt",
  [workflowErrorCode.INVOICE_CLAIMINI_ID_LOCKED]: "Das HDO Ereignis steht für diesen Task bereits fest und kann nicht mehr geändert werden.",
  [workflowErrorCode.INVOICE_CREATE_INCIDENT_NOT_ALLOWED]:
    "Es wurde bereits ein Beleg erfasst – ein neues Ereignis kann für diesen Task nicht mehr angelegt werden.",
  [workflowErrorCode.INVOICE_INCIDENT_NOT_LOCKED]: INVOICE_INCIDENT_NOT_LOCKED_MESSAGE,
  [workflowErrorCode.INVOICE_CLAIMINI_ID_LOCK_MISSING]:
    "Für diesen Task liegen widersprüchliche Daten vor – das gesperrte HDO-Ereignis kann nicht bestimmt werden. Bitte den Support kontaktieren.",
  [workflowErrorCode.INVOICE_LINK_PERSIST_FAILED]:
    "Die Dokumente wurden bereits in eClaim verlinkt, konnten aber nicht in ePort gespeichert werden. Bitte nicht erneut versuchen und den Support kontaktieren.",
  [externalServiceErrorCode.MESSAGE_MANAGER_UNASSIGN_FAILED]: "E-Mail konnte in eClaim nicht freigegeben werden",
  [externalServiceErrorCode.MESSAGE_MANAGER_SERVER_ERROR]:
    "Mail-Service vorübergehend nicht erreichbar – bitte in ein paar Sekunden erneut versuchen",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_PATCH_NOT_FOUND]: "Vorgang in eClaim nicht gefunden",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_PATCH_BAD_REQUEST]: "Ungültige Daten für eClaim-Container",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_PATCH_UNAUTHORIZED]: "Authentifizierung mit eClaim fehlgeschlagen",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_PATCH_FORBIDDEN]: "Keine Berechtigung für diese Aktion in eClaim",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_PATCH_SERVER_ERROR]: "eClaim-Backend vorübergehend nicht erreichbar",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_GET_NOT_FOUND]: "Incident nicht gefunden – Workflow-Informationen konnten nicht geladen werden",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_GET_SERVER_ERROR]:
    "eClaim-Backend vorübergehend nicht erreichbar – Workflow-Informationen konnten nicht geladen werden",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_POST_BAD_REQUEST]: "Ungültige Daten für eClaim-Container",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_POST_UNAUTHORIZED]: "Authentifizierung mit eClaim fehlgeschlagen",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_POST_FORBIDDEN]: "Keine Berechtigung für diese Aktion in eClaim",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_POST_NOT_FOUND]: "Vorgang in eClaim nicht gefunden",
  [externalServiceErrorCode.CLAIMINI_ADAPTER_POST_SERVER_ERROR]: "eClaim-Backend vorübergehend nicht erreichbar",
  // Attachment resolution (s3-adapter)
  [externalServiceErrorCode.S3_ADAPTER_RESOLVE_BAD_REQUEST]: "Ungültige Attachment-URL",
  [externalServiceErrorCode.S3_ADAPTER_RESOLVE_NOT_FOUND]: "Anhang nicht gefunden – die Datei wurde möglicherweise gelöscht",
  [externalServiceErrorCode.S3_ADAPTER_RESOLVE_SERVER_ERROR]:
    "Service vorübergehend nicht erreichbar – bitte später erneut versuchen oder einen Admin kontaktieren",
  [externalServiceErrorCode.S3_ADAPTER_RESOLVE_INVALID_RESPONSE]:
    "Service hat eine ungültige Antwort geliefert – bitte später erneut versuchen oder einen Admin kontaktieren",
};

/** Resolve error code to German message, with fallback */
export function resolveApiError(errorCode: string | undefined, fallback: string): string {
  if (errorCode && errorCode in ERROR_MESSAGES) {
    const msg = ERROR_MESSAGES[errorCode];
    if (msg !== undefined) return msg;
  }
  return fallback;
}
